import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Input, InputNumber, Modal, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useModulePermission } from '../../hooks/usePermission';
import { n502Api, N502ApproveItem, N502Candidate } from '../../services/n502Api';
import '../N501/N501Form.css';
import { BatchAcceptanceBridge, pwn502f1AcceptanceManifest } from '../../features/acceptance';

interface RowState {
  item: N502Candidate;
  selected: boolean;
  opinion: string;
  transfer: boolean;
  compensation: number;
}

const firstLinePresent = (value: string) => value.replace(/\r\n/g, '\n').split('\n')[0].trim() !== '';

const N502Form: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useModulePermission('pwn502f1');
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [broad, setBroad] = useState(false);
  const [rows, setRows] = useState<RowState[]>([]);
  const [level, setLevel] = useState('');
  const [queried, setQueried] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  const selected = useMemo(() => rows.filter((row) => row.selected), [rows]);
  const allSelected = rows.length > 0 && rows.every((row) => row.selected);
  const someSelected = rows.some((row) => row.selected) && !allSelected;

  const query = useCallback(async () => {
    const start = startNo.trim();
    const end = endNo.trim();
    if (start && end && start > end) {
      message.warning('起始客訴單號不可大於結束客訴單號');
      return;
    }
    if (!permissions.canView) {
      message.error('沒有 N502 查詢權限');
      return;
    }
    setLoading(true);
    try {
      const response = await n502Api.query({ start_no: start, end_no: end, broad });
      setRows((response.items || []).map((item) => ({
        item,
        selected: false,
        opinion: '',
        transfer: true,
        compensation: 0,
      })));
      setLevel(response.level || '');
      setQueried(true);
      if (!response.items?.length) message.info('查無可核准的客訴單');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'N502 查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [broad, endNo, permissions.canView, startNo]);

  const patchRow = useCallback((no: string, patch: Partial<RowState>) => {
    setRows((current) => current.map((row) => row.item.no === no ? { ...row, ...patch } : row));
  }, []);

  const execute = useCallback(() => {
    if (!permissions.canApprove) {
      message.error('沒有 N502 核准權限');
      return;
    }
    if (selected.length === 0) {
      message.warning('請先勾選要核准的客訴單');
      return;
    }
    const invalidOpinion = selected.find((row) => !firstLinePresent(row.opinion));
    if (invalidOpinion) {
      message.warning(`客訴單 ${invalidOpinion.item.no} 的核准意見第一行不可空白`);
      return;
    }
    const invalidCompensation = selected.find((row) => row.compensation < 0);
    if (invalidCompensation) {
      message.warning(`客訴單 ${invalidCompensation.item.no} 的賠償金額不可小於 0`);
      return;
    }
    Modal.confirm({
      title: '確認主管核准',
      content: `確定核准已勾選的 ${selected.length} 筆客訴單？整批將以同一交易提交。`,
      okText: '確認核准',
      cancelText: '取消',
      onOk: async () => {
        const items: N502ApproveItem[] = selected.map((row) => ({
          no: row.item.no,
          opinion: row.opinion,
          transfer: row.transfer,
          compensation: row.compensation,
        }));
        setApproving(true);
        try {
          const response = await n502Api.approve(items);
          message.success(`核准完成 ${response.success} 筆`);
          await query();
        } catch (error: any) {
          const data = error.response?.data;
          message.error(data?.error || data?.results?.find((result: any) => result.error)?.error || 'N502 核准失敗，整批未寫入');
        } finally {
          setApproving(false);
        }
      },
    });
  }, [permissions.canApprove, query, selected]);

  const columns: ColumnsType<RowState> = [
    {
      title: <Checkbox checked={allSelected} indeterminate={someSelected} disabled={!permissions.canApprove} onChange={(e) => setRows((current) => current.map((row) => ({ ...row, selected: e.target.checked })))} />,
      key: 'selected', width: 48, align: 'center',
      render: (_, row) => <Checkbox checked={row.selected} disabled={!permissions.canApprove} onChange={(e) => patchRow(row.item.no, { selected: e.target.checked })} />,
    },
    { title: '客訴單號', dataIndex: ['item', 'no'], key: 'no', width: 120 },
    { title: '客戶', dataIndex: ['item', 'customer'], key: 'customer', width: 110 },
    { title: '部門', dataIndex: ['item', 'department'], key: 'department', width: 90 },
    { title: '金額', dataIndex: ['item', 'amount'], key: 'amount', width: 110, align: 'right', render: (value: number) => Number(value || 0).toLocaleString() },
    { title: '目前狀態', dataIndex: ['item', 'status'], key: 'status', width: 90, align: 'center' },
    { title: '目標層級', dataIndex: ['item', 'target_level'], key: 'target_level', width: 90, align: 'center', render: (value: string) => <Tag color="blue">{value}</Tag> },
    {
      title: '核准意見（必填）', key: 'opinion', width: 230,
      render: (_, row) => <Input placeholder={`輸入 ${row.item.no} 核准意見`} value={row.opinion} disabled={!permissions.canApprove} status={row.selected && !firstLinePresent(row.opinion) ? 'error' : undefined} onChange={(e) => patchRow(row.item.no, { opinion: e.target.value })} />,
    },
    {
      title: '轉交運', key: 'transfer', width: 80, align: 'center',
      render: (_, row) => <Checkbox checked={row.transfer} disabled={!permissions.canApprove} onChange={(e) => patchRow(row.item.no, { transfer: e.target.checked })} />,
    },
    {
      title: '賠償金額', key: 'compensation', width: 130,
      render: (_, row) => <InputNumber min={0} precision={2} value={row.compensation} disabled={!permissions.canApprove || row.transfer} onChange={(value) => patchRow(row.item.no, { compensation: value ?? 0 })} />,
    },
  ];

  return (
    <div className="n501-form n502-form">
      {(loading || approving) && <div className="loading-overlay">{approving ? '核准提交中…' : '查詢中…'}</div>}
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline"><a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>ERP</a><span>/</span><span className="current">客訴單主管初核</span></nav>
            <span className="title-separator">—</span><h1>客訴單主管初核</h1><span className="badge">N502</span>
          </div>
        </div>
        <div className="query-bar">
          <label data-contract-field="approveitem.no">客訴單號 從</label>
          <Input aria-label="起始客訴單號" value={startNo} onChange={(e) => setStartNo(e.target.value)} onPressEnter={query} allowClear style={{ width: 160 }} />
          <span className="separator">至</span>
          <Input aria-label="結束客訴單號" value={endNo} onChange={(e) => setEndNo(e.target.value)} onPressEnter={query} allowClear style={{ width: 160 }} />
          <Checkbox checked={broad} disabled={!permissions.canView} onChange={(e) => setBroad(e.target.checked)}>包含較低權限層級</Checkbox>
          <Button type="primary" disabled={!permissions.canView} loading={loading} onClick={query}>確定範圍</Button>
          {level && <Tag color="geekblue">目前核准層級：{level}</Tag>}
        </div>
        <div className="table-card">
          {queried ? <Table<RowState> columns={columns} dataSource={rows} rowKey={(row) => row.item.no} pagination={false} size="small" scroll={{ x: 1250, y: 380 }} locale={{ emptyText: '查無資料' }} /> : <div className="empty-state">請輸入客訴單號範圍後查詢</div>}
        </div>
        <div className="action-bar">
          <Button type="primary" size="large" disabled={!permissions.canApprove || selected.length === 0 || approving} loading={approving} onClick={execute}>主管核准 ({selected.length})</Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>離開</Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwn502f1AcceptanceManifest}
          records={rows}
          selectedKeys={selected.map((row) => row.item.no)}
          keyOf={(row) => row.item.no}
          queryExact={(key) => n502Api.query({ start_no: key, end_no: key, broad: false })}
          lastApiResponse={rows}
          loading={loading || approving}
          onAutoFill={() => rows[0] && setRows((current) => current.map((row, index) => ({ ...row, selected: index === 0 })))}
          autoFillDisabled={rows.length === 0}
          screenDraft={{ startNo, endNo, broad, level }}
        />
      </div>
    </div>
  );
};

export default N502Form;
